import type { serverTimestamp } from 'firebase/firestore'
import { toBoolean } from '../utils/toBoolean'

export interface Configurator {
  dial_size_desc: string | null
  code: string | null
  dial_size_code: string | null
  model_uri: string | null
}

export interface AdditionalImage {
  cdn_key: string
  alt: string | null
}

export interface AdditionalDoc {
  cdn_key: string
  category: string | null
  title: string | null
}

export interface RelatedModel {
  id: string
  model: string | null
  vendor: string | null
  cdn_handle_primary_image: string | null
  category: string | null
  category_detail: string | null
  model_uri: string | null
}

interface TechnicalSpec {
  title: string | null
  specProp: string | null
  specs: Array<string>
}

export interface BaseModel {
  active: boolean
  active_dascosales_web: boolean
  additional_images: Array<AdditionalImage>
  additional_docs: Array<AdditionalDoc>
  applications: Array<object>
  category: string
  category_2: string | null
  configurators: Array<Configurator>
  cdn_handle_datasheet: string | null
  cdn_handle_datasheet_previous: string | null
  cdn_handle_primary_image: string | null
  cdn_handle_primary_image_previous: string | null
  created_date: number | typeof serverTimestamp | null
  created_email: string | null
  created_name: string | null
  created_uid: string | null
  discontinued: boolean
  features: Array<string>
  item_families: Array<string>
  last_modified_date: typeof serverTimestamp | null
  last_modified_email: string | null
  last_modified_name: string | null
  last_modified_uid: string | null
  // last_reviewed_date: typeof serverTimestamp
  // last_reviewed_email: string
  // last_reviewed_name: string
  // last_reviewed_uid: string
  liquid_fillable: boolean
  model: string | null
  model_uri: string | null
  notes: string | null
  overview: string | null
  popularity: number
  product_class_code: string | null
  product_group_code: string | null
  related_models: Array<RelatedModel>
  resources_burping_video: boolean
  resources_videos: Array<any>
  // review_state: string
  // review_state_created_name: string
  // review_state_created_uid: string
  // review_state_created_date: typeof serverTimestamp
  // review_type: string
  vendor: string | null
  vendor_no: string | null
  id?: string | null
}

interface PressureGauge extends BaseModel {
  accuracies: Array<string>
  case_materials: Array<string>
  dial_sizes: Array<string>
  digital: boolean
  gauge_connections: Array<string>
  ranges: Array<string>
  ranges_pressure_groups: Array<string>
  wetted_materials: Array<string>
}

interface DiaphragmSeal extends BaseModel {
  bottom_housing_materials: Array<string>
  diaphragm_materials: Array<string>
  flange_classes: Array<string>
  instrument_connections: Array<string>
  pressure_ratings: Array<string>
  process_connections: Array<string>
}

interface Temperature extends BaseModel {
  accuracies: Array<string>
  bulb_style?: Array<string>
  // case_style: string
  case_materials?: Array<string>
  connections: Array<string>
  connection_locations: Array<string>
  dial_sizes: Array<string>
  digital: boolean
  mounting?: Array<string>
  scale_types: Array<string>
  stem_lengths: Array<string>
  windows: Array<string>
}

interface Thermowell extends BaseModel {
  bore_size: Array<string>
  cap_chain: Array<string>
  flange_facing: Array<string>
  instrument_connections: Array<string>
  lagging: Array<string>
  materials: Array<string>
  process_connections: Array<string>
  rating_class: Array<string>
  shank_style: Array<string>
  surface_finish: Array<string>
  u_dimension: Array<string>
}

// This is the simplest iteration of an Ashcroft B-Series Switch
interface Switch extends BaseModel {
  accuracies: Array<string>
  enclosures: Array<string>
  switch_elements: Array<string>
  actuator_seals: Array<string>
  ranges: Array<string>
  process_connections: Array<string>
  setpoints: Array<string>
  poles_throws: Array<string>
}

interface Trans extends BaseModel {
  accuracies: Array<string>
  electrical_connections: Array<string>
  enclosures: Array<string>
  housing: Array<string>
  measurement_type: Array<string>
  output_signal: Array<string>
  process_connections: Array<string>
  pressure_ranges: Array<string>
  temperature_limits: Array<string>
  wetted_materials: Array<string>
}

interface Accessories extends BaseModel {
  additional_specs: Array<TechnicalSpec>
  instrument_connections: Array<string>
  materials: Array<string>
  process_connections: Array<string>
  wetted_materials: Array<string>
}

function toBaseModelCommon(o: any): BaseModel {
  return {
    id: o.id,
    active: o.active || false,
    active_dascosales_web: o.active_dascosales_web || false,
    additional_images: o.additional_images || [],
    additional_docs: o.additional_docs || [],
    applications: o.applications || [],
    category: o.category,
    category_2: o.category_2 || o.category_detail || null,
    configurators: o.configurators || [],
    cdn_handle_datasheet: o.cdn_handle_datasheet || null,
    cdn_handle_datasheet_previous: o.cdn_handle_datasheet_previous || null,
    cdn_handle_primary_image: o.cdn_handle_primary_image || null,
    cdn_handle_primary_image_previous: o.cdn_handle_primary_image_previous || null,
    created_date: o.created_date || null,
    created_email: o.created_email || null,
    created_name: o.created_name || null,
    created_uid: o.created_uid || null,
    discontinued: o.discontinued || false,
    features: o.features || [],
    item_families: o.item_families || [],
    last_modified_date: o.last_modified_date || null,
    last_modified_email: o.last_modified_email || null,
    last_modified_name: o.last_modified_name || null,
    last_modified_uid: o.last_modified_uid || null,
    liquid_fillable: toBoolean(o.liquid_fillable) || false,
    model: o.model || null,
    model_uri: o.model_uri || null,
    notes: o.notes || null,
    overview: o.overview || null,
    popularity: o.popularity || 1,
    product_class_code: o.product_class_code || null,
    product_group_code: o.product_group_code || null,
    related_models: o.related_models || [],
    resources_burping_video: o.resources_burping_video || false,
    resources_videos: o.resources_videos || [],
    vendor: o.vendor || null,
    vendor_no: o.vendor_no || null
  }
}

function toBaseModelPressureBase(o: any): PressureGauge {
  return {
    ...toBaseModelCommon(o),
    accuracies: o.accuracies || [],
    case_materials: o.case_materials || [],
    dial_sizes: o.dial_sizes || [],
    digital: o.digital || false,
    gauge_connections: o.gauge_connections || [],
    ranges: o.ranges || [],
    ranges_pressure_groups: o.ranges_pressure_groups || [],
    wetted_materials: o.wetted_materials || []
  }
}

function toBaseModelDiaphragmBase(o: any): DiaphragmSeal {
  return {
    ...toBaseModelCommon(o),
    bottom_housing_materials: o.bottom_housing_materials || [],
    diaphragm_materials: o.diaphragm_materials || [],
    flange_classes: o.flange_classes || [],
    instrument_connections: o.instrument_connections || [],
    pressure_ratings: o.pressure_ratings || [],
    process_connections: o.process_connections || []
  }
}

function toBaseModelTempBase(o: any): Temperature {
  return {
    ...toBaseModelCommon(o),
    accuracies: o.accuracies || [],
    dial_sizes: o.dial_sizes || [],
    digital: o.digital || false,
    connections: o.connections || [],
    connection_locations: o.connection_locations || [],
    scale_types: o.scale_types || [],
    stem_lengths: o.stem_lengths || [],
    windows: o.windows || []
  }
}

function toBaseModelThermowellBase(o: any): Thermowell {
  return {
    ...toBaseModelCommon(o),
    bore_size: o.bore_size || [],
    cap_chain: o.cap_chain || [],
    flange_facing: o.flange_facing || [],
    instrument_connections: o.instrument_connections || [],
    lagging: o.lagging || [],
    materials: o.materials || [],
    process_connections: o.process_connections || [],
    rating_class: o.rating_class || [],
    shank_style: o.shank_style || [],
    surface_finish: o.surface_finish || [],
    u_dimension: o.u_dimension || []
  }
}

function toBaseModelSwitchBase(o: any): Switch {
  return {
    ...toBaseModelCommon(o),
    accuracies: o.accuracies || [],
    enclosures: o.enclosures || [],
    switch_elements: o.switch_elements || [],
    actuator_seals: o.actuator_seals || [],
    ranges: o.ranges || [],
    process_connections: o.process_connections || [],
    setpoints: o.setpoints || [],
    poles_throws: o.poles_throws || []
  }
}

function toBaseModelTransBase(o: any): Trans {
  return {
    ...toBaseModelCommon(o),
    accuracies: o.accuracies || [],
    electrical_connections: o.electrical_connections || [],
    enclosures: o.enclosures || [],
    housing: o.housing || null,
    measurement_type: o.measurement_type || null,
    output_signal: o.output_signal || null,
    process_connections: o.process_connections || [],
    pressure_ranges: o.pressure_ranges || [],
    temperature_limits: o.temperature_limits || [],
    wetted_materials: o.wetted_materials || []
  }
}

function toBaseModelAccessoriesBase(o: any): Accessories {
  return {
    ...toBaseModelCommon(o),
    additional_specs: o.additional_specs || [],
    instrument_connections: o.instrument_connections || [],
    materials: o.materials || [],
    process_connections: o.process_connections || [],
    wetted_materials: o.wetted_materials || []
  }
}

export function toBaseModel(o: any): BaseModel {
  const mappings: any = {
    'Pressure Gauge': () => toBaseModelPressureBase(o as PressureGauge),
    'Diaphragm Seal': () => toBaseModelDiaphragmBase(o as DiaphragmSeal),
    'Temperature': () => toBaseModelTempBase(o as Temperature),
    'Thermowell': () => toBaseModelThermowellBase(o as Thermowell),
    'Switch': () => toBaseModelSwitchBase(o as Switch),
    'Transmitter/Transducer': () => toBaseModelTransBase(o as Trans),
    'Accessories': () => toBaseModelAccessoriesBase(o as Accessories)
  }

  return mappings[o.category]() || toBaseModelCommon(o as BaseModel)
}
